/**
 * 檔案用途區塊
 * @module Redirect
 * @purpose 統一處理第三方登入完成後的導回流程，依 query/hash 參數分流至目標網址，並在特定瀏覽器環境補上重載保險機制。
 * @external https://innotech.atlassian.net/browse/IN-52157
 * @external https://innotech.atlassian.net/browse/IN-111885
 * @external https://innotech.atlassian.net/browse/IN-111890
 * @external https://innotech.atlassian.net/browse/IN-89368
 * @external https://innotech.atlassian.net/browse/IN-63960
 * @external https://innotech.atlassian.net/browse/IN-55459
 */
import { safariRedirectReloadDelayMs } from '@shared/constants/time.constants';
import { ThirdpartyLoginMethod } from '@shared/enums';
import { Browser, DevicePlatform } from '@shared/enums/device-platform.enum';
import { detectBrowser, detectOS } from '@utilities/helpers/deviceInfo.helper';
import React, { useEffect, useState } from 'react';

/**
 * 宣告內容用途說明與單號關聯
 * @description Redirect 元件進入頁面後讀取第三方登入回傳參數，依 provider 決定轉址策略，並在 Safari on Mac 且未取得必要參數時啟動延遲重載。
 * @purpose 集中處理 redirect 型 SSO callback，避免各登入供應商在前端回跳行為不一致。
 * @external https://innotech.atlassian.net/browse/IN-52157
 */
const Redirect = () => {
  /**
   * 宣告內容用途說明與單號關聯
   * @description 控制是否啟用補救計時器，當首次解析不到可用導向資訊時觸發後續重載檢查。
   * @purpose 在特定環境下補強 callback 參數延遲可見的情境。
   * @external https://innotech.atlassian.net/browse/IN-63960
   */
  const [isStartTimer, setIsTimerStart] = useState(false);

  useEffect(() => {
    /**
     * 宣告內容用途說明與單號關聯
     * @description 由當前網址 query string 建立參數存取器，供後續登入回傳欄位解析使用。
     * @purpose 以標準 API 一次性解析 callback 參數來源。
     * @external https://innotech.atlassian.net/browse/IN-55459
     */
    const searchParams = new URLSearchParams(location.search);
    /**
     * 宣告內容用途說明與單號關聯
     * @description 兼容不同第三方回傳欄位名稱，優先讀取 code，否則讀取 authCode。
     * @purpose 統一取得可用授權碼以便組裝後續導向網址。
     * @external https://innotech.atlassian.net/browse/IN-89368
     */
    const code = searchParams.get('code') || searchParams.get('authCode'); // google: code, fb: authCode
    /**
     * 宣告內容用途說明與單號關聯
     * @description 讀取 state 作為原目標回跳位址（to）。
     * @purpose 保留登入前導向目的地，供預設分支回傳使用。
     * @external https://innotech.atlassian.net/browse/IN-89368
     */
    const to = searchParams.get('state'); // google, zalo, facebook
    /**
     * 宣告內容用途說明與單號關聯
     * @description 取得 provider 以辨識 callback 所屬第三方登入流程。
     * @purpose 在同一入口分流 Telegram、WalletConnect 與其他預設情境。
     * @external https://innotech.atlassian.net/browse/IN-111885
     */
    const provider = searchParams.get('provider'); // telegram, walletConnect
    /**
     * 宣告內容用途說明與單號關聯
     * @description 取得顯式 redirectUrl，供特定 provider 成功後組裝回跳網址。
     * @purpose 讓登入完成後可回到呼叫端指定頁面。
     * @external https://innotech.atlassian.net/browse/IN-52157
     */
    const redirectUrl = searchParams.get('redirectUrl');

    switch (provider) {
      case ThirdpartyLoginMethod.Telegram:
        /**
         * 宣告內容用途說明與單號關聯
         * @description 從 hash 片段擷取 Telegram 回傳的 tgAuthResult 作為授權碼。
         * @purpose 將 Telegram 特有回傳格式轉換成統一的 authCode 參數。
         * @external https://innotech.atlassian.net/browse/IN-89368
         */
        const tgAuthCode = location.hash.split('tgAuthResult=')[1];
        if (tgAuthCode && redirectUrl) {
          location.href = `${redirectUrl}?provider=${provider}&authCode=${tgAuthCode}`;
        }
        console.log({ authCode: tgAuthCode, redirectUrl, location });
        break;
      case ThirdpartyLoginMethod.WalletConnect:
        // WalletConnect 流程：跳轉到 redirectUrl 並帶上 provider 和 authCode
        /**
         * 宣告內容用途說明與單號關聯
         * @description 將已解析的授權碼映射為 WalletConnect 分支使用的命名。
         * @purpose 在 WalletConnect 導向網址組裝時維持欄位語意清晰。
         * @external https://innotech.atlassian.net/browse/IN-111890
         */
        const walletConnectAuthCode = code;
        if (walletConnectAuthCode && redirectUrl) {
          location.href = `${redirectUrl}?provider=wallet_connect&authCode=${encodeURIComponent(walletConnectAuthCode)}`;
        }
        console.log({ walletConnectAuthCode, redirectUrl, location, provider });
        break;
      default:
        if (to) {
          location.href = code ? `${to}&authCode=${code}` : to;
        } else {
          setIsTimerStart(true);
        }
        break;
    }
    console.log({ authCode: btoa(code ?? ''), to, location });
  }, []);

  useEffect(() => {
    if (isStartTimer && detectOS() === DevicePlatform.Mac && detectBrowser() === Browser.Safari) {
      /**
       * 宣告內容用途說明與單號關聯
       * @description 建立延遲重載計時器，於 Safari on Mac 且啟動旗標為真時重新整理頁面。
       * @purpose 補救 callback 參數未及時出現在網址上的偶發情境。
       * @external https://innotech.atlassian.net/browse/IN-63960
       */
      const timer = setTimeout(() => {
        console.log('timer', location);
        if (isStartTimer) {
          location.reload();
        }
      }, safariRedirectReloadDelayMs);

      return () => clearTimeout(timer);
    }
  }, [isStartTimer]);

  return <></>;
};
export default Redirect;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:21:14.769Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added required three-section annotation layout for TSX file, inserted declaration-level JSDoc blocks for mapped signatures using only provided origin tickets, formatted all @external tags as full Jira browse URLs, and kept runtime logic unchanged.
 */
